import { useState, useEffect, useRef, useMemo } from "react";

import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Upload, Eye, Code, Columns2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { skillsMarketplaceApi } from "@/lib/api/skills-marketplace";

const SKILL_MD_PLACEHOLDER = `# My Skill Name

## Overview
Describe what this skill does and how it helps the AI agent.

## Rules
- Key rule or behavior 1
- Key rule or behavior 2

## How to Use
Explain when and how to use this skill in chat.`;

type MdMode = "edit" | "split" | "preview";

const MODES: { key: MdMode; icon: React.ReactNode; label: string }[] = [
  { key: "edit",    icon: <Code className="h-3 w-3" />,    label: "Raw" },
  { key: "split",   icon: <Columns2 className="h-3 w-3" />, label: "Split" },
  { key: "preview", icon: <Eye className="h-3 w-3" />,     label: "Rendered" },
];

/* ── Warp-style colours ── */
const CORAL = "#e8796e";

export default function CustomSkillEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [mdMode, setMdMode] = useState<MdMode>("split");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /* ── line count for gutter ── */
  const lineCount = useMemo(() => {
    if (!content) return 1;
    return content.split("\n").length;
  }, [content]);

  /* ── strip YAML frontmatter for rendered preview ── */
  const renderedContent = useMemo(() => {
    const trimmed = content.trimStart();
    if (!trimmed.startsWith("---")) return content;
    const end = trimmed.indexOf("---", 3);
    if (end === -1) return content;
    return trimmed.slice(end + 3).trimStart();
  }, [content]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".md") && file.type !== "text/markdown" && file.type !== "text/plain") {
      toast.error("Please upload a .md file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      if (typeof text === "string") setContent(text);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    skillsMarketplaceApi
      .getCustomSkill(id)
      .then((skill) => {
        setName(skill.name);
        setDescription(skill.description ?? "");
        setContent(skill.content);
      })
      .catch(() => {
        toast.error("Skill not found");
        navigate("/dashboard/skills");
      })
      .finally(() => setIsLoading(false));
  }, [id, navigate]);

  const handleSave = async (): Promise<void> => {
    if (!name.trim()) { toast.error("Skill name is required"); return; }
    if (!content.trim()) { toast.error("SKILL.md content is required"); return; }
    setIsSaving(true);
    try {
      if (isEdit && id) {
        await skillsMarketplaceApi.updateCustomSkill(id, { name: name.trim(), description: description.trim() || undefined, content });
        toast.success("Skill updated");
      } else {
        await skillsMarketplaceApi.createCustomSkill({ name: name.trim(), description: description.trim() || undefined, content });
        toast.success("Skill created");
      }
      navigate("/dashboard/skills");
    } catch (err: unknown) {
      const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(axiosMsg || (err instanceof Error ? err.message : "Failed to save skill"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id) return;
    try {
      await skillsMarketplaceApi.deleteCustomSkill(id);
      toast.success("Skill deleted");
      navigate("/dashboard/skills");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to delete skill");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-center justify-center py-32">
          <div className="h-8 w-8 rounded-full border-2 border-violet-500/20 border-t-violet-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">

      {/* Back */}
      <button
        onClick={() => navigate("/dashboard/skills")}
        className="group inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-7 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
        Back to Skills
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium bg-violet-500/10 text-violet-500 border border-violet-500/20">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
              Custom Skill
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {isEdit ? "Edit Skill" : "New Skill"}
          </h1>
        </div>

        {isEdit && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 gap-1.5 mt-1">
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Custom Skill</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete &ldquo;{name}&rdquo;? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="space-y-5">

        {/* Name + Description */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="skill-name" className="text-xs font-medium text-muted-foreground">
              Skill Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="skill-name"
              name="name"
              placeholder="e.g. My API Helper"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-muted/30 border-border/60 focus-visible:border-violet-500/50 focus-visible:ring-1 focus-visible:ring-violet-500/30"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="skill-description" className="text-xs font-medium text-muted-foreground">
              Description
            </Label>
            <Input
              id="skill-description"
              name="description"
              placeholder="Brief description of what this skill does…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-muted/30 border-border/60 focus-visible:border-violet-500/50 focus-visible:ring-1 focus-visible:ring-violet-500/30"
            />
          </div>
        </div>

        {/* Markdown editor */}
        <div className="space-y-2">
          {/* Toolbar row */}
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium text-muted-foreground">
              SKILL.md Content <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-2">
              {/* Pill toggle — Warp uses "Rendered" / "Raw" labels */}
              <div className="flex items-center p-0.5 rounded-lg bg-muted/50 border border-border/50 gap-0.5">
                {MODES.map(({ key, icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setMdMode(key)}
                    className={cn(
                      "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-150",
                      mdMode === key
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {icon}
                    {label}
                  </button>
                ))}
              </div>
              {/* Upload button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground border border-border/60 hover:border-border bg-transparent hover:bg-muted/50 transition-all"
              >
                <Upload className="h-3.5 w-3.5" />
                Upload .md
              </button>
            </div>
          </div>

          <input ref={fileInputRef} type="file" accept=".md,text/markdown,text/plain" className="hidden" onChange={handleFileUpload} />

          {/* Editor surface — fixed height, scroll inside */}
          <div
            className={cn(
              "rounded-xl border border-border/60 overflow-hidden",
              "shadow-[0_2px_16px_-4px_hsl(0_0%_0%/0.12)]",
              mdMode === "split" ? "grid grid-cols-2" : "flex flex-col",
            )}
            style={{ height: "calc(100vh - 340px)", minHeight: 300 }}
          >
            {/* ── Raw editor pane (with line numbers like Warp) ── */}
            {(mdMode === "edit" || mdMode === "split") && (
              <div className={cn(
                "flex flex-col min-w-0 min-h-0",
                mdMode === "split" && "border-r border-border/50",
              )}>
                {mdMode === "split" && (
                  <div className="flex items-center gap-2 px-4 py-2 border-b border-border/40 bg-muted/20 shrink-0">
                    <Code className="h-3 w-3 text-muted-foreground/50" />
                    <span className="text-[10px] font-mono font-semibold text-muted-foreground/50 tracking-widest uppercase">
                      Raw
                    </span>
                  </div>
                )}
                <div className="flex-1 min-h-0 overflow-auto bg-[hsl(var(--card))]">
                  <div className="flex min-h-full">
                    {/* Line numbers gutter — inside the same scroll container */}
                    <div
                      className="select-none sticky left-0 shrink-0 pt-4 pb-4 pr-2 pl-3 text-right border-r border-border/30 bg-[hsl(var(--card))]"
                      style={{ width: 48 }}
                      aria-hidden="true"
                    >
                      {Array.from({ length: Math.max(lineCount, 20) }, (_, i) => (
                        <div
                          key={i}
                          className="font-mono text-[13px] leading-[1.7] text-muted-foreground/30"
                        >
                          {i + 1}
                        </div>
                      ))}
                    </div>
                    {/* Textarea */}
                    <textarea
                      ref={textareaRef}
                      id="skill-content"
                      name="content"
                      placeholder={SKILL_MD_PLACEHOLDER}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      className={cn(
                        "flex-1 resize-none border-0 outline-none ring-0 bg-transparent",
                        "font-mono text-[13px] leading-[1.7]",
                        "text-foreground/85 placeholder:text-muted-foreground/25",
                        "p-4",
                      )}
                      style={{ minHeight: "100%" }}
                      spellCheck={false}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── Preview pane (Warp-style rendered markdown) ── */}
            {(mdMode === "preview" || mdMode === "split") && (
              <div className="flex flex-col min-w-0 min-h-0 bg-[hsl(var(--background))]">
                {mdMode === "split" && (
                  <div className="flex items-center gap-2 px-4 py-2 border-b border-border/40 bg-muted/20 shrink-0">
                    <Eye className="h-3 w-3 text-muted-foreground/50" />
                    <span className="text-[10px] font-semibold text-muted-foreground/50 tracking-widest uppercase">
                      Rendered
                    </span>
                  </div>
                )}
                <div className="flex-1 min-h-0 overflow-auto px-8 py-6">
                  {renderedContent.trim() ? (
                    <div className="max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          /* ── Headings ── */
                          h1: ({ children }) => (
                            <h1 className="text-[26px] font-bold text-foreground mt-0 mb-5 leading-tight">
                              {children}
                            </h1>
                          ),
                          h2: ({ children }) => (
                            <h2 className="text-[20px] font-bold mt-8 mb-3 leading-snug" style={{ color: CORAL }}>
                              {children}
                            </h2>
                          ),
                          h3: ({ children }) => (
                            <h3 className="text-[16px] font-semibold mt-6 mb-2 leading-snug text-foreground/90">
                              {children}
                            </h3>
                          ),
                          h4: ({ children }) => (
                            <h4 className="text-[14px] font-semibold mt-5 mb-1.5 text-foreground/80">
                              {children}
                            </h4>
                          ),

                          /* ── Body ── */
                          p: ({ children }) => (
                            <p className="text-[14px] leading-[1.7] text-foreground/80 my-2.5">
                              {children}
                            </p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-bold text-foreground">{children}</strong>
                          ),
                          em: ({ children }) => (
                            <em className="italic text-foreground/70">{children}</em>
                          ),

                          /* ── Lists ── */
                          ul: ({ children, className }) => (
                            <ul className={cn(
                              "my-2 space-y-1",
                              className?.includes("contains-task-list") ? "pl-0 list-none" : "pl-5 list-disc",
                            )}>
                              {children}
                            </ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="my-2 space-y-1 pl-5 list-decimal">
                              {children}
                            </ol>
                          ),
                          li: ({ children, className }) => {
                            const isTask = className?.includes("task-list-item");
                            return (
                              <li className={cn(
                                "text-[14px] leading-[1.7] text-foreground/80",
                                isTask && "flex items-start gap-2 list-none",
                              )}>
                                {children}
                              </li>
                            );
                          },

                          /* ── Checkboxes (Warp renders - [ ] as real checkboxes) ── */
                          input: ({ type, checked, ...props }) => {
                            if (type === "checkbox") {
                              return (
                                <input
                                  type="checkbox"
                                  checked={checked ?? false}
                                  disabled
                                  className="mt-[5px] shrink-0 cursor-default"
                                  style={{ width: 14, height: 14, appearance: "none", WebkitAppearance: "none", border: "1.5px solid hsl(var(--foreground) / 0.3)", borderRadius: 2, backgroundColor: checked ? CORAL : "transparent", borderColor: checked ? CORAL : undefined }}
                                  {...props}
                                />
                              );
                            }
                            return <input type={type} {...props} />;
                          },

                          /* ── Code ── */
                          code: ({ children, className }) => {
                            const isBlock = className?.includes("language-");
                            if (isBlock) {
                              return (
                                <code className="block font-mono text-[12.5px] leading-relaxed text-foreground/80">
                                  {children}
                                </code>
                              );
                            }
                            return (
                              <code
                                className="font-mono text-[12.5px] px-1.5 py-0.5 rounded"
                                style={{
                                  backgroundColor: `${CORAL}18`,
                                  color: CORAL,
                                }}
                              >
                                {children}
                              </code>
                            );
                          },
                          pre: ({ children }) => (
                            <pre className="my-3 rounded-lg bg-muted/50 border border-border/40 px-4 py-3.5 overflow-x-auto">
                              {children}
                            </pre>
                          ),

                          /* ── Block elements ── */
                          blockquote: ({ children }) => (
                            <blockquote className="my-4 pl-4 border-l-[3px] text-foreground/60 italic" style={{ borderColor: `${CORAL}80` }}>
                              {children}
                            </blockquote>
                          ),
                          hr: () => <hr className="my-6 border-border/30" />,
                          a: ({ children, href }) => (
                            <a
                              href={href}
                              className="underline underline-offset-2"
                              style={{ color: CORAL }}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {children}
                            </a>
                          ),

                          /* ── Tables ── */
                          table: ({ children }) => (
                            <div className="my-4 overflow-x-auto rounded-lg border border-border/40">
                              <table className="w-full text-[13px] border-collapse">{children}</table>
                            </div>
                          ),
                          thead: ({ children }) => (
                            <thead className="bg-muted/30 border-b border-border/40">{children}</thead>
                          ),
                          th: ({ children }) => (
                            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {children}
                            </th>
                          ),
                          td: ({ children }) => (
                            <td className="px-3 py-2 border-b border-border/15 text-foreground/75 text-[13px]">
                              {children}
                            </td>
                          ),
                          tr: ({ children }) => (
                            <tr className="border-b border-border/10 last:border-0">{children}</tr>
                          ),
                          tbody: ({ children }) => (
                            <tbody>{children}</tbody>
                          ),

                          /* ── Inline extras ── */
                          del: ({ children }) => (
                            <del className="text-foreground/50 line-through">{children}</del>
                          ),
                          img: ({ src, alt }) => (
                            <img
                              src={src}
                              alt={alt ?? ""}
                              className="my-3 max-w-full rounded-lg border border-border/30"
                              loading="lazy"
                            />
                          ),

                          /* ── Remaining headings ── */
                          h5: ({ children }) => (
                            <h5 className="text-[13px] font-semibold mt-4 mb-1 text-foreground/75">
                              {children}
                            </h5>
                          ),
                          h6: ({ children }) => (
                            <h6 className="text-[12px] font-semibold mt-3 mb-1 text-muted-foreground uppercase tracking-wide">
                              {children}
                            </h6>
                          ),
                        }}
                      >
                        {renderedContent}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-3 py-20 text-center">
                      <div className="h-10 w-10 rounded-xl bg-muted/50 flex items-center justify-center">
                        <Eye className="h-4 w-4 text-muted-foreground/40" />
                      </div>
                      <p className="text-sm text-muted-foreground/50">Nothing to preview yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground/60">
            Write markdown instructions for your AI agent, or upload an existing SKILL.md file. Synced to your dev container automatically.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-border/40">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard/skills")}
            disabled={isSaving}
            className="text-muted-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-violet-600 hover:bg-violet-500 text-white min-w-[100px] gap-2"
          >
            {isSaving ? (
              <>
                <span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                Saving…
              </>
            ) : "Save Skill"}
          </Button>
        </div>
      </div>
    </div>
  );
}
